import React, { useState, useEffect } from 'react';
import { Dumbbell, Calendar, Plus, Minus } from 'lucide-react';

const MINUTES_PER_EXERCISE = 10;

const ExercisesSlider = ({ value, onChange }) => {
  const min = 4;
  const max = 12;
  const step = 1;
  const percentage = ((value - min) / (max - min)) * 100;
  const approxMinutes = value * MINUTES_PER_EXERCISE;

  return (
    <div className="w-full space-y-4">
      <div className="flex justify-center">
        <span className="text-2xl font-extrabold aw-title">
          {value} exercises
        </span>
      </div>

      <div className="relative w-full h-6 flex items-center">
        <div className="absolute w-full h-2 rounded-full aw-progress-track" />
        <div
          className="absolute h-2 rounded-full aw-progress-fill"
          style={{ width: `${percentage}%` }}
        />

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="absolute w-full appearance-none bg-transparent cursor-pointer"
          style={{
            '--range-thumb-size': '24px',
            '--range-track-height': '0px',
          }}
        />

        <div
          className="absolute w-6 h-6 rounded-full pointer-events-none transition-all duration-200 ease-out aw-slider-thumb"
          style={{ left: `calc(${percentage}% - 12px)` }}
        />
      </div>

      <p className="text-center text-sm text-gray-500">
        Approximate total time: ~{approxMinutes} minutes
        <br />
        <span className="text-xs text-gray-400">(may be longer if your session includes cardio)</span>
      </p>
    </div>
  );
};

const StepFour = ({ formData, updateFields }) => {
  const daysPerWeek = [2, 3, 4, 5, 6];
  
  // Set initial days per week to 2 if not already set
  useEffect(() => {
    if (!formData.daysPerWeek) {
      updateFields({ daysPerWeek: 2 });
    }
  }, []);

  const [currentIndex, setCurrentIndex] = useState(
    formData.daysPerWeek ? daysPerWeek.findIndex(days => days === formData.daysPerWeek) : 0
  );
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNext = () => {
    if (isAnimating || currentIndex === daysPerWeek.length - 1) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex + 1);
    updateFields({ daysPerWeek: daysPerWeek[currentIndex + 1] });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handlePrev = () => {
    if (isAnimating || currentIndex === 0) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex - 1);
    updateFields({ daysPerWeek: daysPerWeek[currentIndex - 1] });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleCardClick = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    updateFields({ daysPerWeek: daysPerWeek[index] });
    setTimeout(() => setIsAnimating(false), 300);
  };

  return (
    <div className="space-y-8 w-full">
      {/* Days per Week Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Calendar className="text-red-600" size={24} />
          <label className="block text-lg aw-label">
            Days per Week
          </label>
        </div>
        <p className="text-sm text-gray-500">
          How many days per week would you like to work out?
        </p>

        {/* Days Carousel */}
        <div className="relative h-32 rounded-2xl px-2"
          style={{
            background: 'linear-gradient(180deg, #f7f7f9 0%, #ececef 100%)',
            border: '1px solid #d8d8dc',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.7)'
          }}
        >
          {/* Edge fades */}
          <div className="absolute left-0 top-0 h-full w-20 rounded-l-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to right, #f7f7f9, rgba(247,247,249,0))' }} />
          <div className="absolute right-0 top-0 h-full w-20 rounded-r-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to left, #ececef, rgba(236,236,239,0))' }} />

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`aw-round-btn absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === 0 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <Minus className="w-5 h-5" />
          </button>

          <button
            onClick={handleNext}
            disabled={currentIndex === daysPerWeek.length - 1}
            className={`aw-round-btn absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === daysPerWeek.length - 1 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <Plus className="w-5 h-5" />
          </button>

          {/* Carousel */}
          <div className="relative w-full h-full overflow-hidden px-14">
            <div className="w-full h-full flex items-center justify-center">
              {daysPerWeek.map((days, index) => {
                const position = index - currentIndex;
                const selected = position === 0;
                return (
                  <div
                    key={days}
                    onClick={() => handleCardClick(index)}
                    className={`absolute w-28 transition-all duration-300 ease-in-out
                      ${Math.abs(position) <= 1 ? 'cursor-pointer' : ''}`}
                    style={{
                      transform: `translateX(${position * 110}%) scale(${selected ? 1 : 0.8})`,
                      opacity: Math.abs(position) > 1 ? 0 : 1 - Math.abs(position) * 0.5,
                      zIndex: 10 - Math.abs(position),
                      pointerEvents: Math.abs(position) <= 1 ? 'auto' : 'none'
                    }}
                  >
                    <div className={`aw-card-option ${selected ? 'aw-card-option-selected' : 'aw-card-option-dark'}`}>
                      <div className="flex flex-col items-center">
                        <span className="text-3xl font-extrabold">{days}</span>
                        <span className={`text-sm ${selected ? 'text-white/85' : 'text-gray-300'}`}>days</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Exercises per Day Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Dumbbell className="text-red-600" size={24} />
          <label className="block text-lg aw-label">
            Exercises per Day
          </label>
        </div>
        <p className="text-sm text-gray-500">
          How many exercises do you want in each workout?
        </p>
        <div className="px-4 py-6">
          <ExercisesSlider
            value={formData.exercisesPerDay || 6}
            onChange={(value) => updateFields({
              exercisesPerDay: value,
              timePerSession: value * MINUTES_PER_EXERCISE
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default StepFour;